import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Megaphone, Radar, Send, ArrowLeft, ShieldCheck, Sparkles, Copy, CheckCircle, Archive, Inbox, Mic, MicOff, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

/** Browser speech-to-text (Web Speech API). Returns null if unsupported. */
function createRecognizer(onText, onEnd) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const rec = new SR();
  rec.lang = "en-GB";
  rec.continuous = true;
  rec.interimResults = true;
  rec.onresult = (e) => {
    let final = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      if (e.results[i].isFinal) final += e.results[i][0].transcript;
    }
    if (final) onText(final);
  };
  rec.onend = () => onEnd && onEnd();
  rec.onerror = () => onEnd && onEnd();
  return rec;
}

/** Mic button that appends dictated text into a textarea's state. */
function DictateButton({ onAppend }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!supported) return null;

  const toggle = () => {
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = createRecognizer(
      (text) => onAppend(text.replace(/^\s+/, "").trim() ? " " + text.trim() : ""),
      () => setListening(false),
    );
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
      toast.success("Listening… speak your instruction, tap again to stop.");
    } catch { setListening(false); }
  };

  return (
    <button type="button" onClick={toggle} data-testid="dictate-btn"
      title={listening ? "Stop dictation" : "Dictate by voice"}
      className={`absolute right-2 top-2 p-1.5 rounded-full transition-colors ${
        listening ? "bg-red-100 text-red-600 animate-pulse" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
      {listening ? <MicOff size={14} /> : <Mic size={14} />}
    </button>
  );
}

/**
 * ADMIN — Your AI Team. Three draft-only digital employees (GPT-5.2) with a live
 * business snapshot + founder's standing instructions + a review queue.
 * They draft, the founder approves — nothing is ever sent by an agent.
 */

const ICONS = { exec: ClipboardList, marketing: Megaphone, ops: Radar };
const PLATFORMS = [
  { id: "mavula", label: "Mavula" },
  { id: "pula-connect", label: "Pula Connect" },
  { id: "pula-learn", label: "Pula Learn" },
  { id: "all", label: "All platforms" },
];
const STARTERS = {
  exec: ["Give me my morning brief", "What needs my attention today?", "How is the waiting list growing?"],
  marketing: ["Draft a WhatsApp post for Mavula's waiting list", "Write a Pula Learn launch teaser for care workers", "Draft a Pula Connect community welcome post"],
  ops: ["Run the operations scan", "What is going stale right now?", "Draft a chase list for this week"],
};

export default function AiTeamPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [active, setActive] = useState("exec");
  const [view, setView] = useState("chat"); // chat | queue | settings
  const [sessions, setSessions] = useState({});
  const [threads, setThreads] = useState({});
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(null);
  const [scan, setScan] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [draftCounts, setDraftCounts] = useState({ pending: 0, approved: 0, archived: 0 });
  const [queueFilter, setQueueFilter] = useState("pending");
  const [editing, setEditing] = useState({}); // draftId -> text
  const [saveAs, setSaveAs] = useState({});   // msgIndex -> platform
  const [instr, setInstr] = useState({ global_notes: "", exec_notes: "", marketing_notes: "", ops_notes: "" });
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/admin/ai-team/agents")
      .then(({ data }) => setAgents(data.agents))
      .catch((err) => {
        if ([401, 403].includes(err?.response?.status)) navigate("/admin");
        else toast.error("Could not load the AI team.");
      });
    loadDrafts("pending");
    api.get("/admin/ai-team/instructions").then(({ data }) => setInstr(data)).catch(() => {});
  }, [navigate]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [threads, active]);

  async function loadDrafts(status) {
    try {
      const { data } = await api.get(`/admin/ai-team/drafts?status=${status}`);
      setDrafts(data.drafts);
      setDraftCounts(data.counts);
    } catch { /* quiet */ }
  }

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setThreads((t) => ({ ...t, [active]: [...(t[active] || []), { role: "user", content: message }] }));
    try {
      const { data } = await api.post("/admin/ai-team/chat", {
        agent: active, message, session_id: sessions[active] || null,
      });
      setSessions((s) => ({ ...s, [active]: data.session_id }));
      setThreads((t) => ({ ...t, [active]: [...(t[active] || []), { role: "assistant", content: data.reply }] }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The agent is unavailable — try again in a moment.");
    } finally { setBusy(false); }
  };

  const getBrief = async () => {
    setBusy(true); setBrief(null);
    try {
      const { data } = await api.get("/admin/ai-team/brief");
      setBrief(data.brief);
    } catch { toast.error("Brief failed — try again shortly."); }
    finally { setBusy(false); }
  };

  const runScan = async () => {
    setBusy(true); setScan(null);
    try {
      const { data } = await api.get("/admin/ai-team/ops-scan");
      setScan(data);
    } catch { toast.error("Scan failed — try again shortly."); }
    finally { setBusy(false); }
  };

  const saveToQueue = async (content, msgIdx) => {
    try {
      await api.post("/admin/ai-team/drafts", { agent: active, platform: saveAs[msgIdx] || "all", content });
      toast.success("Filed to review queue.");
      loadDrafts(queueFilter);
    } catch { toast.error("Could not save draft."); }
  };

  const patchDraft = async (id, payload) => {
    try {
      await api.patch(`/admin/ai-team/drafts/${id}`, payload);
      if (payload.status === "approved") toast.success("Approved — copy it when you're ready to post.");
      loadDrafts(queueFilter);
    } catch { toast.error("Update failed."); }
  };

  const copyDraft = (content) => {
    navigator.clipboard.writeText(content).then(() => toast.success("Copied — paste it into WhatsApp or your channel."));
  };

  const saveInstructions = async () => {
    try {
      await api.put("/admin/ai-team/instructions", instr);
      toast.success("Standing instructions saved — the team will use them from now on.");
    } catch { toast.error("Could not save instructions."); }
  };

  const thread = threads[active] || [];

  return (
    <main className="bg-white min-h-[70vh] py-10" data-testid="ai-team-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <Link to="/admin" className="text-sm mv-green-text inline-flex items-center gap-1 mb-6 hover:underline" data-testid="ai-team-back">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Sparkles size={24} className="mv-gold-text" /> Your AI Team
          </h1>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-200" data-testid="draft-only-badge">
            <ShieldCheck size={13} /> Draft-only — nothing sends without you
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-6 max-w-2xl">
          Three digital employees covering Mavula Investment, Pula Connect and Pula Learn.
          They see live business numbers and draft; you approve everything.
        </p>

        {/* View tabs */}
        <div className="flex gap-2 mb-8 border-b mv-hairline" data-testid="ai-team-tabs">
          {[
            { id: "chat", label: "Chat" },
            { id: "queue", label: `Review queue (${draftCounts.pending})`, icon: Inbox },
            { id: "settings", label: "Standing instructions" },
          ].map((t) => (
            <button key={t.id} onClick={() => { setView(t.id); if (t.id === "queue") loadDrafts(queueFilter); }}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                view === t.id ? "border-[var(--mv-gold)] mv-green-text" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {view === "chat" && (
          <>
            {/* Agent cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {agents.map((a) => {
                const Icon = ICONS[a.id] || ClipboardList;
                const isActive = active === a.id;
                return (
                  <button key={a.id} onClick={() => setActive(a.id)} data-testid={`agent-card-${a.id}`}
                    className={`text-left p-5 rounded-xl border transition-all ${isActive ? "mv-green-bg text-white border-transparent shadow-lg" : "bg-white mv-hairline hover:shadow-md"}`}>
                    <Icon size={24} className={isActive ? "mv-gold-text mb-3" : "mv-green-text mb-3"} />
                    <h2 className="font-bold text-base">{a.name}</h2>
                    <p className={`text-xs mt-1 ${isActive ? "text-white/75" : "text-gray-500"}`}>{a.role}</p>
                  </button>
                );
              })}
            </div>

            {active === "exec" && (
              <div className="mb-6">
                <button onClick={getBrief} disabled={busy} data-testid="morning-brief-btn"
                  className="btn-gold px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60">
                  {busy ? "Thinking…" : "Generate morning brief"}
                </button>
                {brief && (
                  <div className="mt-4 bg-gray-50 border mv-hairline rounded-xl p-5" data-testid="morning-brief-output">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{brief}</pre>
                    <div className="mt-3 flex items-center gap-2">
                      <select value={saveAs["brief"] || "all"} onChange={(e) => setSaveAs({ ...saveAs, brief: e.target.value })}
                        className="text-xs border mv-hairline rounded px-2 py-1.5 bg-white" data-testid="brief-platform-select">
                        {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                      </select>
                      <button onClick={() => saveToQueue(brief, "brief")} className="text-xs mv-green-text underline" data-testid="brief-save-btn">
                        File to review queue
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {active === "ops" && (
              <div className="mb-6">
                <button onClick={runScan} disabled={busy} data-testid="ops-scan-btn"
                  className="btn-gold px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60">
                  {busy ? "Scanning…" : "Run operations scan"}
                </button>
                {scan && (
                  <div className="mt-4 border mv-hairline rounded-xl overflow-hidden" data-testid="ops-scan-output">
                    <p className="text-xs text-gray-500 px-4 py-2 bg-gray-50">{scan.count} item{scan.count === 1 ? "" : "s"} need attention</p>
                    {scan.items.length === 0 && <p className="px-4 py-6 text-sm text-gray-500">All clear — nothing stale right now.</p>}
                    {scan.items.map((it, i) => (
                      <div key={i} className="px-4 py-3 border-t mv-hairline text-sm flex flex-wrap gap-x-4 gap-y-1" data-testid={`ops-item-${i}`}>
                        <span className="font-semibold">{it.who || "—"}</span>
                        <span className="text-gray-500">{it.type.replace(/_/g, " ")} · since {it.since || "?"}</span>
                        <span className="mv-green-text">{it.action}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Chat */}
            <div className="border mv-hairline rounded-xl overflow-hidden" data-testid={`chat-panel-${active}`}>
              <div className="px-4 py-3 bg-gray-50 border-b mv-hairline flex flex-wrap gap-2">
                {(STARTERS[active] || []).map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border mv-hairline hover:bg-gray-100 transition-colors"
                    data-testid={`starter-${active}-${s.slice(0, 16).replace(/\W+/g, "-").toLowerCase()}`}>
                    {s}
                  </button>
                ))}
              </div>
              <div className="h-80 overflow-y-auto p-4 space-y-3" data-testid={`chat-messages-${active}`}>
                {thread.length === 0 && (
                  <p className="text-sm text-gray-400 text-center mt-24">
                    Say something to your {agents.find((a) => a.id === active)?.name || "agent"}…
                  </p>
                )}
                {thread.map((m, i) => (
                  <div key={i} className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                    m.role === "user" ? "ml-auto mv-green-bg text-white" : "bg-gray-100 text-gray-800"}`}>
                    <div className="whitespace-pre-wrap">{m.content}</div>
                    {m.role === "assistant" && (
                      <div className="mt-2 flex items-center gap-2 opacity-80">
                        <select value={saveAs[i] || "all"} onChange={(e) => setSaveAs({ ...saveAs, [i]: e.target.value })}
                          className="text-xs border mv-hairline rounded px-1.5 py-1 bg-white" data-testid={`msg-platform-${i}`}>
                          {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                        <button onClick={() => saveToQueue(m.content, i)} className="text-xs mv-green-text underline" data-testid={`msg-save-${i}`}>
                          File to review queue
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {busy && <p className="text-xs text-gray-400">typing…</p>}
                <div ref={bottomRef} />
              </div>
              <div className="flex gap-2 p-3 border-t mv-hairline">
                <input value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={`Message ${agents.find((a) => a.id === active)?.name || "agent"}…`}
                  data-testid={`chat-input-${active}`}
                  className="flex-1 border mv-hairline rounded-lg px-3 py-2.5 text-sm" />
                <button onClick={() => send()} disabled={busy} data-testid={`chat-send-${active}`}
                  className="btn-green px-4 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-60">
                  <Send size={15} /> Send
                </button>
              </div>
            </div>
          </>
        )}

        {view === "queue" && (
          <div data-testid="review-queue">
            <div className="flex gap-2 mb-4">
              {["pending", "approved", "archived"].map((s) => (
                <button key={s} onClick={() => { setQueueFilter(s); loadDrafts(s); }}
                  data-testid={`queue-filter-${s}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    queueFilter === s ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-gray-50"}`}>
                  {s} ({draftCounts[s]})
                </button>
              ))}
            </div>
            {drafts.length === 0 && (
              <p className="text-sm text-gray-500 py-10 text-center" data-testid="queue-empty">
                No {queueFilter} drafts. In Chat, use "File to review queue" under any agent reply.
              </p>
            )}
            <div className="space-y-4">
              {drafts.map((d) => (
                <div key={d.id} className="border mv-hairline rounded-xl p-4" data-testid={`draft-${d.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 capitalize">{d.agent}</span>
                    <span className="px-2 py-0.5 rounded-full mv-green-bg text-white">{PLATFORMS.find((p) => p.id === d.platform)?.label || d.platform}</span>
                    <span className="text-gray-400">{d.created_at?.slice(0, 16).replace("T", " ")}</span>
                    {(d.history || []).length > 0 && (
                      <span className="text-gray-400" data-testid={`draft-history-${d.id}`}
                        title={(d.history || []).map((h) => `${h.action} by ${h.by} at ${h.at?.slice(0, 16).replace("T", " ")}`).join("\n")}>
                        · {d.history[d.history.length - 1].action} by {d.history[d.history.length - 1].by}
                      </span>
                    )}
                    <span className={`ml-auto font-semibold ${d.status === "approved" ? "text-emerald-700" : d.status === "archived" ? "text-gray-400" : "mv-gold-text"}`}>
                      {d.status}
                    </span>
                  </div>
                  <textarea
                    value={editing[d.id] ?? d.content}
                    onChange={(e) => setEditing({ ...editing, [d.id]: e.target.value })}
                    rows={Math.min(10, Math.max(3, d.content.split("\n").length))}
                    className="w-full text-sm border mv-hairline rounded-lg p-3 font-sans whitespace-pre-wrap"
                    data-testid={`draft-content-${d.id}`} />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {editing[d.id] !== undefined && editing[d.id] !== d.content && (
                      <button onClick={() => { patchDraft(d.id, { content: editing[d.id] }); setEditing({ ...editing, [d.id]: undefined }); }}
                        className="text-xs px-3 py-1.5 rounded-lg border mv-hairline hover:bg-gray-50" data-testid={`draft-save-edit-${d.id}`}>
                        Save edit
                      </button>
                    )}
                    <button onClick={() => copyDraft(editing[d.id] ?? d.content)}
                      className="text-xs px-3 py-1.5 rounded-lg border mv-hairline hover:bg-gray-50 inline-flex items-center gap-1" data-testid={`draft-copy-${d.id}`}>
                      <Copy size={12} /> Copy
                    </button>
                    {d.status === "approved" && (
                      <a href={`https://wa.me/?text=${encodeURIComponent(editing[d.id] ?? d.content)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1"
                        style={{ backgroundColor: "#25D366" }} data-testid={`draft-whatsapp-${d.id}`}>
                        <MessageCircle size={12} /> Send via WhatsApp
                      </a>
                    )}
                    {d.status !== "approved" && (
                      <button onClick={() => patchDraft(d.id, { status: "approved" })}
                        className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white inline-flex items-center gap-1" data-testid={`draft-approve-${d.id}`}>
                        <CheckCircle size={12} /> Approve
                      </button>
                    )}
                    {d.status !== "archived" && (
                      <button onClick={() => patchDraft(d.id, { status: "archived" })}
                        className="text-xs px-3 py-1.5 rounded-lg border mv-hairline text-gray-500 hover:bg-gray-50 inline-flex items-center gap-1" data-testid={`draft-archive-${d.id}`}>
                        <Archive size={12} /> Archive
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "settings" && (
          <div className="max-w-2xl" data-testid="instructions-panel">
            <p className="text-sm text-gray-600 mb-5">
              Standing instructions are injected into every agent conversation and morning brief — teach the team
              how you actually work (e.g. "I review on Mondays and Thursdays", "WhatsApp first, always end with a question").
            </p>
            {[
              ["global_notes", "For the whole team (applies always)"],
              ["exec_notes", "For the Executive Assistant only"],
              ["marketing_notes", "For Marketing only"],
              ["ops_notes", "For Operations only"],
            ].map(([key, label]) => (
              <label key={key} className="block mb-4">
                <span className="text-xs font-semibold text-gray-600">{label}</span>
                <span className="relative block mt-1">
                  <DictateButton onAppend={(t) => setInstr((cur) => ({ ...cur, [key]: ((cur[key] || "") + t).slice(0, 4000) }))} />
                  <textarea value={instr[key] || ""} rows={3} maxLength={4000}
                    onChange={(e) => setInstr({ ...instr, [key]: e.target.value })}
                    className="w-full border mv-hairline rounded-lg px-3 py-2.5 pr-9 text-sm"
                    data-testid={`instr-${key}`} />
                </span>
              </label>
            ))}
            <button onClick={saveInstructions} className="btn-green px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors" data-testid="instr-save-btn">
              Save standing instructions
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
